import { Component, ViewEncapsulation, HostListener, OnInit, OnDestroy, DoCheck, HostBinding, ChangeDetectorRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Menu, NavService } from '../../services/nav.service';
import { LayoutService } from '../../services/layout.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpHelpService } from 'src/app/Data/service/http-help.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class SidebarComponent implements OnInit, OnDestroy, DoCheck {
	// Properties
	public menuItems: Menu[];
	public sidebarItems: any[] = [];
	public curentLang: string = "";
	public allPrivilige: any = []; // Consider typing this more strictly if possible

	public userRegistId: any;
	public userType: any;

	// For Horizontal Menu
	public margin: number = 0;
	public width: number = window.innerWidth;
	public leftArrowNone: boolean = true;
	public rightArrowNone: boolean = false;

	private readonly destroy$ = new Subject<void>();

	@HostBinding('class.sidebar-is-collapsed')
	get sidebarIsCollapsed(): boolean {
		return this.navServices.collapseSidebar;
	}

	constructor(
		private router: Router,
		public navServices: NavService,
		private service: HttpHelpService,
		private spinner: NgxSpinnerService,
		public layout: LayoutService,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef
	) {
		this.navServices.items.subscribe(menuItems => {
			this.menuItems = menuItems;
			this.router.events.subscribe((event) => {
				if (event instanceof NavigationEnd) {
					this.menuItems.forEach(item => {
						this.checkActivePath(item, event.url);
					});
				}
			});
		});
		this.curentLang = localStorage.getItem('curentLang') || 'en';
	}

	ngOnInit(): void {
		this.userRegistId = JSON.parse(localStorage.getItem('user_id') || 'null');
		this.userType = localStorage.getItem('userType_en') || ''; // Prefer direct assignment or default
		this.curentLang = localStorage.getItem('curentLang') || 'en';
		this.getAllPrivilge();

		this.navServices.sidebarLeftMenuRefresh
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.userType = localStorage.getItem('userType_en') || '';
				if (this.allPrivilige?.children) {
					this.updateSidebarItems();
				}
			});

		this.navServices.collapseSidebar$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.cdr.markForCheck());

		window.addEventListener('storage', (event) => {
			if (event.key === 'curentLang') {
				this.curentLang = event.newValue || 'en';
				// setTimeout might be used to ensure change detection after an out-of-Angular event
				setTimeout(() => this.updateSidebarItems());
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngDoCheck(): void {
		// This lifecycle hook runs frequently; consider performance implications
		const lang = localStorage.getItem('curentLang') || 'en';
		if (lang !== this.curentLang) {
			this.curentLang = lang;
			this.updateSidebarItems();
		}
	}

	@HostListener('window:resize', ['$event'])
	onResize(event: Event): void {
		if (event.target instanceof Window) {
			this.width = event.target.innerWidth - 500; // Consider making 500 a constant or configurable
		}
	}

	private checkActivePath(item: Menu, url: string): void {
		if (item.path === url) {
			this.setNavActive(item);
			return; // Found, no need to check children
		}
		if (item.children) {
			item.children.forEach(subItem => {
				this.checkActivePath(subItem, url); // Recurse for children
			});
		}
	}
	
	// Active Nav state
	private setNavActive(activeItem: Menu): void {
		this.menuItems.forEach(menuItem => {
			menuItem.active = (menuItem === activeItem); // Deactivate others

			if (menuItem.children) {
				let parentActive = false;
				menuItem.children.forEach(subMenuItem => {
					subMenuItem.active = (subMenuItem === activeItem); // Deactivate other sub-items
					if (subMenuItem === activeItem) {
						parentActive = true;
						menuItem.active = true; // Activate parent
					}

					if (subMenuItem.children) {
						let subParentActive = false;
						subMenuItem.children.forEach(subSubMenuItem => {
							subSubMenuItem.active = (subSubMenuItem === activeItem);
							if (subSubMenuItem === activeItem) {
								subParentActive = true;
								subMenuItem.active = true; // Activate parent
								menuItem.active = true;    // Activate grandparent
							}
						});
						if(subParentActive) subMenuItem.active = true;
					}
				});
				if(parentActive) menuItem.active = true;
			}
		});
	}


	// Click Toggle menu
	public toggletNavActive(item: Menu): void {
		// This logic assumes only one branch of the menu can be active.
		// If item is already active, toggling it will make it inactive.
		// If item is not active, it will become active, and others might need deactivation.
		const currentActiveState = item.active;
	
		// Deactivate all items first (optional, depends on desired UX)
		// this.menuItems.forEach(mi => {
		//   mi.active = false;
		//   if (mi.children) {
		//     mi.children.forEach(smi => {
		//       smi.active = false;
		//       if (smi.children) {
		//         smi.children.forEach(ssmi => ssmi.active = false);
		//       }
		//     });
		//   }
		// });
	
		if (!currentActiveState) {
			// If we are activating a new item, deactivate others (simplified)
			this.menuItems.forEach(a => {
				if (a !== item && !this.isParentOf(a, item)) {
					a.active = false;
				}
				if (a.children) {
					a.children.forEach(b => {
						if (b !== item && !this.isParentOf(b, item)) {
							b.active = false;
						}
						if (b.children) {
							b.children.forEach(c => {
								if (c !== item) {
									c.active = false;
								}
							});
						}
					});
				}
			});
		}
	
		item.active = !currentActiveState;
	
		// Ensure parents of the toggled item reflect the active state
		if (item.active) {
			this.activateParents(item);
		}
	}
	
	private isParentOf(parentCandidate: Menu, child: Menu): boolean {
		if (!parentCandidate.children) {
			return false;
		}
		if (parentCandidate.children.includes(child)) {
			return true;
		}
		return parentCandidate.children.some(subChild => this.isParentOf(subChild, child));
	}
	
	private activateParents(item: Menu): void {
		this.menuItems.forEach(menuItem => {
			if (menuItem.children) {
				if (menuItem.children.includes(item)) {
					menuItem.active = true;
				}
				menuItem.children.forEach(subMenuItem => {
					if (subMenuItem.children && subMenuItem.children.includes(item)) {
						subMenuItem.active = true;
						menuItem.active = true; // Also activate grandparent
					}
				});
			}
		});
	}

	public sidebarToggle(): void {
		this.navServices.toggleSidebar();
	}

	getToggleTooltip(collapsed: boolean): string {
		if (this.curentLang === 'ar') {
			return collapsed ? 'توسيع القائمة' : 'طي القائمة';
		}
		if (this.curentLang === 'he') {
			return collapsed ? 'הרחב תפריט' : 'כווץ תפריט';
		}
		return collapsed ? 'Expand menu' : 'Collapse menu';
	}

	getToggleIconClass(collapsed: boolean): string {
		const rtl = this.layout.config.settings.layout_type === 'rtl';
		if (collapsed) {
			return rtl ? 'fa-angle-left' : 'fa-angle-right';
		}
		return rtl ? 'fa-angle-right' : 'fa-angle-left';
	}

	// For Horizontal Menu
	public scrollToLeft(): void {
		// Consider making magic numbers (-this.width, 0) configurable or constants
		if (this.margin >= -this.width) { // This condition seems to imply margin is usually negative
			this.margin = 0;
			this.leftArrowNone = true;
			this.rightArrowNone = false;
		} else {
			this.margin += this.width;
			this.rightArrowNone = false;
		}
	}

	public scrollToRight(): void {
		// Consider making magic numbers (-3051, -3464) configurable or constants
		const minMargin = -3051; // Example: define as constant
		const finalMargin = -3464; // Example: define as constant

		if (this.margin <= minMargin) {
			this.margin = finalMargin;
			this.leftArrowNone = false;
			this.rightArrowNone = true;
		} else {
			this.margin -= this.width; // Assuming scroll right means decreasing margin (more negative)
			this.leftArrowNone = false;
		}
	}

	public getAllPrivilge(): void {
		this.spinner.show();
		this.service.getPrivileges().subscribe({
			next: (res: any) => { // Consider typing 'res' more strictly
				this.allPrivilige = res;
				localStorage.setItem('allPrivilige', JSON.stringify(this.allPrivilige));
				this.updateSidebarItems();
				this.spinner.hide();
			},
			error: (error: any) => {
				// console.error('Failed to get privileges:', error); // Good practice to log errors
				this.spinner.hide();
			}
		});
	}

	public updateSidebarItems(): void {
		if (!this.allPrivilige || !this.allPrivilige.children) {
			return;
		}
		const mapped = this.allPrivilige.children.map((item: any) => {
			const title = this.getTitleByLang(item);
			const route = item.route as string | undefined;
			const isInvoicesV2 = !!(route && route.toLowerCase().includes('/invoices-v2'));
			const isBeta = false;
			const isExpenses = !!(route && route.includes('/expenses'));
			let iconClass = item.icon;
			if (isExpenses) {
				iconClass = '<i class="icofont icofont-brain-alt" aria-hidden="true"></i>';
			} else if (isInvoicesV2) {
				iconClass = '<i class="icofont icofont-bill-alt" aria-hidden="true"></i>';
			}
			return {
				title,
				route,
				iconClass,
				isBeta,
			};
		});

		const hasDashboardHome = mapped.some((item) => item.route?.includes('/dashboard/dashboard'));
		this.sidebarItems = mapped.filter((item) => {
			if (!item.route) return true;
			const routeLower = item.route.toLowerCase();
			if (routeLower.includes('/dashboard/statistics')) return false;
			// Merchants: hide standalone statistics/analytics entries — dashboard is the home.
			if (this.isMerchantUser() && hasDashboardHome) {
				const statsAr =
					item.title?.includes('إحصائيات') ||
					item.title?.includes('الأحصائيات') ||
					item.title?.includes('Statistics') ||
					item.title?.includes('סטטיסטיקות') ||
					item.title?.includes('التحليلات') ||
					item.title?.includes('Analytics');
				if (statsAr && routeLower.includes('/dashboard') && !routeLower.includes('/dashboard/dashboard')) {
					return false;
				}
				// Hide merchant-info home entry; reports stay via openReports query param.
				if (
					routeLower.includes('/merchant-info') &&
					!(item as { queryParams?: Record<string, string> }).queryParams?.openReports
				) {
					return false;
				}
			}
			const statsAr = item.title?.includes('إحصائيات') || item.title?.includes('الأحصائيات');
			if (hasDashboardHome && statsAr && routeLower.includes('/dashboard')) {
				return routeLower.includes('/dashboard/dashboard');
			}
			return true;
		});
		this.appendMerchantOnlyMenuItems();
		this.appendNotificationsMenuItem();
	}

	/** Notifications hub — visible for all authenticated users. */
	private appendNotificationsMenuItem(): void {
		const route = '/dashboard/notifications';
		const exists = this.sidebarItems.some((item) =>
			(item.route || '').toLowerCase().includes('/notifications'),
		);
		if (exists) return;

		const entry = {
			title: this.translate.instant('menu.notifications'),
			route,
			iconClass: '<i class="fa fa-bell" aria-hidden="true"></i>',
			isBeta: false,
		};

		const dashboardIdx = this.sidebarItems.findIndex((item) =>
			(item.route || '').toLowerCase().includes('/dashboard/dashboard'),
		);
		if (dashboardIdx >= 0) {
			this.sidebarItems.splice(dashboardIdx + 1, 0, entry);
		} else {
			this.sidebarItems.unshift(entry);
		}
	}

	/** Merchant roles (100/101) — items not yet in privileges API. */
	private isMerchantUser(): boolean {
		const typeEn = (this.userType || localStorage.getItem('userType_en') || '').toLowerCase();
		if (typeEn === 'admin') return false;
		const typeNum = Number(localStorage.getItem('user_type'));
		return typeNum === 100 || typeNum === 101;
	}

	private appendMerchantOnlyMenuItems(): void {
		if (!this.isMerchantUser()) return;

		const extras: Array<{
			route: string;
			iconClass: string;
			titleKey: string;
			queryParams?: Record<string, string>;
			insertNear?: 'products' | 'expenses' | 'invoices' | 'home' | 'customers';
		}> = [
			{
				route: '/dashboard/company-template-designer',
				iconClass: '<i class="fa fa-paint-brush" aria-hidden="true"></i>',
				titleKey: 'menu.companyTemplateDesigner',
			},
			{
				route: '/dashboard/merchant-info',
				queryParams: { openReports: '1' },
				iconClass: '<i class="fa fa-file-text-o" aria-hidden="true"></i>',
				titleKey: 'menu.reports',
				insertNear: 'expenses',
			},
			{
				route: '/dashboard/merchant-product-categories',
				iconClass: '<i class="fa fa-tags" aria-hidden="true"></i>',
				titleKey: 'menu.merchantProductCategories',
				insertNear: 'products',
			},
			{
				route: '/dashboard/fee-agreements',
				iconClass: '<i class="fa fa-handshake-o" aria-hidden="true"></i>',
				titleKey: 'menu.feeAgreements',
				insertNear: 'customers',
			},
		];

		for (const extra of extras) {
			const exists = this.sidebarItems.some((item) => {
				const route = (item.route || '').toLowerCase();
				if (extra.queryParams?.openReports === '1') {
					return route.includes('/merchant-info') && item.queryParams?.openReports === '1';
				}
				return route === extra.route.toLowerCase();
			});
			if (exists) continue;

			const insertAfterDashboard = this.sidebarItems.findIndex((item) =>
				(item.route || '').toLowerCase().includes('/dashboard/dashboard'),
			);
			const insertAfterExpenses = this.sidebarItems.findIndex((item) =>
				(item.route || '').toLowerCase().includes('/expenses'),
			);
			const insertAfterInvoices = this.sidebarItems.findIndex((item) =>
				(item.route || '').toLowerCase().includes('/invoices-v2'),
			);
			const insertAfterProducts = this.sidebarItems.findIndex((item) =>
				(item.route || '').toLowerCase().endsWith('/products') ||
				(item.route || '').toLowerCase().includes('/dashboard/products'),
			);
			const insertAfterCustomers = this.sidebarItems.findIndex((item) => {
				const route = (item.route || '').toLowerCase();
				return route.endsWith('/customers') || route.includes('/dashboard/customers');
			});

			let insertIndex = -1;
			if (extra.insertNear === 'home' && insertAfterDashboard >= 0) {
				insertIndex = insertAfterDashboard + 1;
			} else if (extra.insertNear === 'expenses' && insertAfterExpenses >= 0) {
				insertIndex = insertAfterExpenses + 1;
			} else if (extra.insertNear === 'products' && insertAfterProducts >= 0) {
				insertIndex = insertAfterProducts + 1;
			} else if (extra.insertNear === 'customers' && insertAfterCustomers >= 0) {
				insertIndex = insertAfterCustomers + 1;
			} else if (insertAfterInvoices >= 0) {
				insertIndex = insertAfterInvoices + 1;
			}

			const entry = {
				title: this.translate.instant(extra.titleKey),
				route: extra.route,
				queryParams: extra.queryParams,
				iconClass: extra.iconClass,
				isBeta: false,
			};
			if (insertIndex >= 0) {
				this.sidebarItems.splice(insertIndex, 0, entry);
			} else {
				this.sidebarItems.push(entry);
			}
		}
	}

	private getTitleByLang(item: any): string { // Consider typing 'item'
		// Check if this is the expenses route and use translation service
		if (item.route && item.route.includes('/expenses')) {
			return this.translate.instant('menu.expenses');
		}
		if (item.route && item.route.includes('/reports-gain-loss')) {
			return this.translate.instant('menu.reportsGainLoss');
		}
		if (item.route && item.route.includes('/business-insights')) {
			return this.translate.instant('menu.businessInsights');
		}
		if (item.route && item.route.includes('/dashboard/dashboard')) {
			return this.isMerchantUser()
				? this.translate.instant('menu.homeScreen')
				: this.translate.instant('menu.analytics');
		}
		if (item.route && item.route.includes('/dashboard/merchant-info')) {
			const qp = (item as { queryParams?: Record<string, string> }).queryParams;
			if (qp?.openReports === '1') {
				return this.translate.instant('menu.reports');
			}
			return this.translate.instant('menu.homeScreen');
		}
		if (item.route && (item.route.includes('/merchant-tasks') || item.route.includes('/tasks/merchant') || item.route.endsWith('/tasks'))) {
			return this.translate.instant('tasks.title');
		}
		if (item.route && (item.route.includes('/admin-tasks') || item.route.includes('/tasks/admin'))) {
			return this.translate.instant('tasks.adminTitle');
		}
		if (item.route && (item.route.includes('/office-tasks') || item.route.includes('/tasks/office'))) {
			return this.translate.instant('tasks.officeTitle');
		}
		if (item.route && item.route.toLowerCase().includes('/invoices-v2')) {
			// Base word only; “V2” is rendered as superscript in the sidebar template
			return this.translate.instant('menu.invoicesV2Base');
		}
		if (item.route && (item.route.includes('/customers') || item.route.endsWith('/customers'))) {
			return this.translate.instant('menu.customers');
		}
		if (item.route && item.route.includes('/fee-agreements')) {
			return this.translate.instant('menu.feeAgreements');
		}
		if (item.route && item.route.includes('/company-template-designer')) {
			return this.translate.instant('menu.companyTemplateDesigner');
		}
		if (item.route && item.route.includes('/merchant-product-categories')) {
			return this.translate.instant('menu.merchantProductCategories');
		}
		if (item.route && item.route.includes('/merchant-storage')) {
			return this.translate.instant('menu.merchantStorage');
		}
		if (item.route && /\/users\/?$/i.test(String(item.route).replace(/\/$/, ''))) {
			return this.translate.instant('menu.users');
		}
		if (item.route && /\/companies\/?$/i.test(String(item.route).replace(/\/$/, ''))) {
			return this.translate.instant('menu.companies');
		}
		if (item.route && item.route.includes('/permanent-file')) {
			return this.translate.instant('permanentFile.title');
		}

		// For other items, use the backend translations
		switch (this.curentLang) {
			case 'ar': return item.title_ar;
			case 'he': return item.title_he;
			default: return item.title;
		}
	}
}
